<template>
  <div class="section__block section__block--centered" v-if="!isFormSend && !formSendError">
    <h2 class="headline headline--h2">{{ t('contact.headline') }}</h2>
  </div>
  <div class="section__block" v-if="!isFormSend && !formSendError">
    <div class="row justify-content-center">
      <div class="col col-lg-8">
        <form class="form form--initialized" @submit.prevent="handleSubmit">
          <div class="form__body">
            <div class="form__control" role="group">
              <div class="form__control-content form__control-content--gap--16">
                <div class="form__row">
                  <div class="row">
                    <div class="col-12 col-lg-6">
                      <InputField :label="t('contact.firstname.label')"
                                  id="contact-firstname"
                                  v-model="formData.firstname"
                                  @validation-change="handleFirstNameValidation"
                                  :force-validation="forceValidationOnSubmit"
                                  :rules="firstnameRules"
                                  :placeholder="t('contact.firstname.placeholder')"
                                  autocomplete="given-name" />
                    </div>
                    <div class="col-12 col-lg-6">
                      <InputField :label="t('contact.lastname.label')"
                                  id="contact-lastname"
                                  v-model="formData.lastname"
                                  @validation-change="handleLastNameValidation"
                                  :force-validation="forceValidationOnSubmit"
                                  :rules="lastnameRules"
                                  :placeholder="t('contact.lastname.placeholder')"
                                  autocomplete="family-name" />
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12 col-lg-6">
                      <InputField :label="t('contact.email.label')"
                                  id="contact-email"
                                  v-model="formData.email"
                                  type="email"
                                  @validation-change="handleEmailValidation"
                                  :force-validation="forceValidationOnSubmit"
                                  :rules="emailRules"
                                  :placeholder="t('contact.email.placeholder')"
                                  autocomplete="email" />
                    </div>
                    <div class="col-12 col-lg-6">
                      <InputField :label="t('contact.phone.label')"
                                  id="contact-phone"
                                  v-model="formData.phone"
                                  :placeholder="t('contact.phone.placeholder')"
                                  autocomplete="tel" />
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <SelectField :label="t('contact.subject.label')"
                                   id="contact-subject"
                                   v-model="formData.subject"
                                   :options="tm('contact.subject.options')"
                                   @validation-change="handleSubjectValidation"
                                   :force-validation="forceValidationOnSubmit"
                                   :large="true"
                                   :rules="subjectRules" />
                    </div>
                  </div>
                  <div class="row" v-if="showStudioSelect">
                    <div class="col-12">
                      <LocationFinderSelect v-model="locationfinder"
                                            :studios="apiJson.studios"
                                            @validation-change="handleStudioValidation"
                                            :force-validation="forceValidationOnSubmit"
                                            :rules="studioRules"></LocationFinderSelect>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <TextareaField :label="t('contact.message.label')"
                                     id="contact-message"
                                     v-model="formData.message"
                                     @validation-change="handleMessageValidation"
                                     :force-validation="forceValidationOnSubmit"
                                     :rules="messageRules"
                                     :placeholder="t('contact.message.placeholder')" />
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <Checkbox id="contact-privacy"
                                v-model="formData.privacy"
                                @validation-change="handlePrivacyValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="privacyRules"
                                :helptext="t('contact.privacy.helptext')"
                                :label="t('contact.privacy.label')"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="form__footer-panel">
            <div class="form__row form__row--text-center">
              <Submit :loading="isSubmitLoading"
                      :label="t('contact.submit.label')" />
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div class="section__block section__block--centered" ref="success" aria-live="polite" v-if="isFormSend && !formSendError">
    <h2 class="headline headline--h2">{{ t('contact.thanks.title') }}</h2>
    <p class="paragraph">{{ t('contact.thanks.copy') }}</p>
  </div>
  <div class="section__block section__block--centered" ref="error" aria-live="polite" v-if="!isFormSend && formSendError">
    <h2 class="headline headline--h2">{{ t('contact.error.title') }}</h2>
    <p class="paragraph">{{ t('contact.error.copy') }}</p>
  </div>
</template>

<script setup>
import {reactive, ref, computed, onMounted, useTemplateRef} from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import InputField from './components/fields/InputField.vue';
import Submit from './components/fields/Submit.vue';
import {getCountryAndLanguagePathFromUrl, getURLParameterByName} from '../../abstracts/utilities.js';
import {getStudioUidByName, getStudioByName, trackData} from './utilities.js';
import SelectField from './components/fields/SelectField.vue';
import TextareaField from './components/fields/TextareaField.vue';
import LocationFinderSelect from './components/fields/LocationFinderSelect.vue';
import Checkbox from './components/fields/Checkbox.vue';

const { t, tm } = useI18n();
const success = useTemplateRef('success');
const error = useTemplateRef('error');

const locationfinder = ref('');

const formData = reactive({
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  subject: '',
  studio: '',
  message: '',
  privacy: false
})

const props = defineProps({
  apiJson: {
    type: Object,
    default: () => {}
  }
})

const isValidFirstName = ref(false);
const isValidLastName = ref(false);
const isValidEmail = ref(false);
const isValidSubject = ref(true);
const isValidStudio = ref(true);
const isValidMessage = ref(false);
const isValidPrivacy = ref(false);

const isFormSend = ref(false);
const formSendError = ref(false);
const isSubmitLoading = ref(false);

const forceValidationOnSubmit = ref(false);

const firstnameRules = [(value) => value.length > 0 || t('contact.firstname.error.empty') ];
const lastnameRules = [(value) => value.length > 0 || t('contact.lastname.error.empty') ];
const emailRules = [
  (value) => value.length > 0 || t('newsletter.email.error.empty'),
  (value) => /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value) || t('newsletter.email.error.email'),
];
const subjectRules = [(value) => value.length > 0 || t('contact.subject.error.empty') ];
const studioRules = [
  (value) => (formData.subject == 4 && value.length > 0) || t('contact.studio.error.empty'),
  (value) => Object.keys(getStudioByName(props.apiJson, value)).length > 0 || t('et.studio.error.studio')
];
const messageRules = [(value) => value.length > 0 || t('contact.message.error.empty') ];
const privacyRules = [(value) => value === true || t('contact.privacy.error.empty') ];

const showStudioSelect = computed(() => formData.subject == 4);

onMounted(() => {
  if(!getURLParameterByName('studio')?.length) return;
  formData.subject = '4';
});

const handleFirstNameValidation = (isValid) => {isValidFirstName.value = isValid;};
const handleLastNameValidation = (isValid) => {isValidLastName.value = isValid;};
const handleEmailValidation = (isValid) => {isValidEmail.value = isValid;};
const handleSubjectValidation = (isValid) => {
  isValidSubject.value = isValid;

  if(formData.subject != 4) {
    isValidStudio.value = true;
  }
};
const handleStudioValidation = (isValid) => {isValidMessage.value = isValid;};
const handleMessageValidation = (isValid) => {isValidMessage.value = isValid;};
const handlePrivacyValidation = (isValid) => {isValidPrivacy.value = isValid;};

const isFormValid = computed(() => isValidFirstName.value && isValidLastName.value && isValidEmail.value && isValidSubject.value && isValidStudio.value && isValidMessage.value && isValidPrivacy.value);

const handleSubmit = () => {
  forceValidationOnSubmit.value = true;
  isSubmitLoading.value = true;

  setTimeout(() => {
    if (isFormValid.value) {

      // set studio id only for message to studio subject
      if(formData.subject == 4) {
        formData.studio = getStudioUidByName(props.apiJson, locationfinder.value);
      }

      axios.post(window.location.origin + '/' + getCountryAndLanguagePathFromUrl() + '/send/contact/', formData).then(response => {
        console.info(response.status);

        isFormSend.value = true;
        formSendError.value = false;

        if(formData.subject == 4) {
          trackData({
            'event': 'generate_lead',
            'form_name': 'Kontakt',
            'studio_id': '<f:format.raw>{studio.uid}</f:format.raw>',
            'studio_name': '<f:format.raw>{studio.name}</f:format.raw>'
          });
        } else {
          trackData({
            'event': 'generate_lead',
            'form_name': 'Kontakt'
          });
        }
      }).catch(error => {
        console.error(error.status);

        isFormSend.value = false;
        formSendError.value = true;
      });

    } else {
      isSubmitLoading.value = false;
    }
  }, 0);
};

</script>
